/* Hoperings — "Pure Silver" material option (native implementation of cw-silver)
 * =============================================================================
 * v2.0.0 · 25 Jul 2026
 *
 * Reads its configuration from the JSON script tag rendered by
 * snippets/cw-silver-option.liquid, which the SHARED product-variant-picker
 * renders on any product that has a silver twin — so this runs on whatever
 * template the product already uses, with no per-product section clone.
 *
 * Selecting the silver swatch:
 *   - repoints every add-to-cart form of this section at the silver variant
 *   - swaps price / compare-at / discount badge (Markets-safe: the money format
 *     is derived from a Liquid-rendered sample)
 *   - swaps the sub-title, the first "Details" tab box, the FAQ answers, the
 *     gallery / thumbnail / lightbox images and the description image block
 *   - hides the quantity-tier savings bar (steel-only pricing)
 * Selecting steel restores every original value captured on first run.
 *
 * -----------------------------------------------------------------------------
 * WHAT CHANGED IN v2 (and why)
 * -----------------------------------------------------------------------------
 * v1 watched the whole document — `new MutationObserver(...)` on
 * `document.documentElement` with `{childList:true, subtree:true}` — and ran a
 * full re-apply *synchronously* inside the callback, plus a 250 ms interval
 * ticking 40 times. On a PDP with ~15 DOM-mutating apps (Vitals, Loox,
 * Klaviyo, Customily, Gobot, Nostra, Appstle, UploadKit …) that is thousands of
 * full-document passes: the page went unresponsive, buttons took seconds to
 * react, and the jQuery `slideToggle` FAQ was rewritten mid-animation so
 * answers jumped or refused to open. The injected (Intelligems) build of the
 * same test shared the code and could freeze the page outright.
 *
 * v2:
 *   · observes ONLY <product-info>, coalesces every trigger into one
 *     requestAnimationFrame pass and disconnects while it writes, so our own
 *     writes can never re-trigger us;
 *   · no polling interval at all;
 *   · circuit breaker — more than 25 passes in a second and the observer is
 *     dropped for good (the swatch keeps working through its own events);
 *   · every write is idempotent and null-safe;
 *   · FAQ / gallery / image block are re-applied only when the material
 *     actually changes (or when the theme re-renders them away), so the
 *     accordion animation is left alone;
 *   · the swatch click is one delegated capture-phase listener, so it survives
 *     Dawn re-rendering product-info;
 *   · originals are captured field by field, the first time each element
 *     exists, so switching back to steel always restores everything.
 *
 * -----------------------------------------------------------------------------
 * WHAT CHANGED IN v3 (metafield-driven rollout)
 * -----------------------------------------------------------------------------
 * v2 booted from a `data-cw-silver-section` attribute that only the two cloned
 * silver sections carried, so a new silver twin meant a new section + a new
 * template. v3:
 *   · boots from the config script itself and derives its scope from the
 *     enclosing `.shopify-section`, so it works inside the shared variant
 *     picker on ANY product section;
 *   · de-duplicates per section id, so a Section-Rendering-API re-render that
 *     re-emits the config (and re-executes this file) reuses the live instance
 *     instead of stacking a second controller on the same PDP;
 *   · hides the quantity-savings bar from JS (`hideQtyBar`) instead of needing a
 *     `cw-hide-on-silver` class hand-added to each cloned section;
 *   · gates the steel-only copy edits (the "Extra Savings On 2+ Items" icon line
 *     and the quantity-discount FAQ) on `qtyGatedCopy`: they are applied only
 *     when the product does NOT price by quantity, which is exactly when they
 *     would otherwise be untrue on silver. Previously each template had to be
 *     told by hand, and getting it wrong shipped false copy.
 *
 * Debug: `window.__cwSilverDebug = true`, or `__cwSilverInstances[0].debug()`.
 * ===========================================================================*/
(function () {
  'use strict';

  var VERSION = '3.0.0';
  var ATTR = 'data-cw-silver-ready';
  var MAX_PASSES_PER_SECOND = 25;
  var MAX_PAGE_RETRIES = 40;

  window.__cwSilverInstances = window.__cwSilverInstances || [];
  /* One controller per section id — survives this file being re-executed by a
     section re-render that re-inserts the <script> tag. */
  window.__cwSilverBySection = window.__cwSilverBySection || {};

  /* ==========================================================================
   * Helpers
   * ======================================================================== */

  function log() {
    if (!window.__cwSilverDebug) return;
    try { console.log.apply(console, ['[cw-silver]'].concat([].slice.call(arguments))); } catch (e) {}
  }
  function warn(e) {
    try { console.warn('[cw-silver] ' + (e && e.message ? e.message : e)); } catch (x) {}
  }
  function qs(sel, root) {
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  }
  function qsa(sel, root) {
    try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); } catch (e) { return []; }
  }
  /* Idempotent, null-safe writers. */
  function setText(el, value) {
    if (!el || value == null || value === '') return false;
    if (el.textContent.trim() === String(value).trim()) return false;
    el.textContent = ' ' + value + ' ';
    return true;
  }
  function setHTML(el, value) {
    if (!el || value == null) return false;
    if (el.innerHTML === value) return false;
    el.innerHTML = value;
    return true;
  }
  function setAttr(el, name, value) {
    if (!el || value == null || value === '' || value === 'null') return false;
    if (el.getAttribute(name) === value) return false;
    el.setAttribute(name, value);
    return true;
  }
  function raf(fn) {
    var done = false;
    var call = function () { if (!done) { done = true; fn(); } };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(call);
    setTimeout(call, 120);                    // rAF is paused in background tabs
  }

  /* ---------- money (Markets-safe) ---------- */

  function parseNum(txt) {
    var m = String(txt).replace(/\s/g, '').match(/\d[\d.,]*/);
    if (!m) return null;
    var raw = m[0], dec = '', intPart = raw;
    var lastSep = Math.max(raw.lastIndexOf('.'), raw.lastIndexOf(','));
    if (lastSep > -1 && raw.length - lastSep - 1 <= 2) {
      dec = raw.slice(lastSep + 1);
      intPart = raw.slice(0, lastSep);
    }
    var n = parseFloat(intPart.replace(/[.,]/g, '') + '.' + (dec || '0'));
    if (isNaN(n)) return null;
    return {
      value: n,
      decimals: dec.length,
      decSep: dec.length ? raw.charAt(lastSep) : '.',
      thouSep: dec.length ? (raw.charAt(lastSep) === '.' ? ',' : '.') : ',',
      raw: raw
    };
  }

  /* Formats `value` (major units) using the separators / affixes of `sample`. */
  function formatLike(sample, value) {
    var s = parseNum(sample);
    if (!s) return null;
    var fixed = value.toFixed(s.decimals);
    var parts = fixed.split('.');
    var intStr = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, s.thouSep);
    var out = intStr + (parts[1] ? s.decSep + parts[1] : '');
    return String(sample).replace(s.raw, out);
  }

  /* ==========================================================================
   * Controller
   * ======================================================================== */

  function Silver(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.pi = qs('product-info', root) || qs('product-info');
    this.state = 'steel';
    this.orig = {};
    this.steelVariantId = null;
    this.mo = null;
    this.observerAllowed = false;
    this.writing = false;
    this.queued = false;
    this.degraded = false;
    this.passes = [];
    this.totalPasses = 0;
    this.pageDone = { faq: null, gallery: null, block: null };
    this.pageRetries = 0;
    this.priceState = 'steel';
  }

  /* `root` is the enclosing .shopify-section when there is one, else document. */
  Silver.prototype.contains = function (el) {
    if (!el) return false;
    if (this.root === document) return document.contains(el);
    return this.root.contains(el);
  };

  Silver.prototype.money = function (cents) {
    return formatLike(this.cfg.moneySample, cents / 100);
  };

  /* Quantity in the buy box (the theme's savings bar prices per total). */
  Silver.prototype.qty = function () {
    if (!this.cfg.qtyPricing) return 1;
    var inp = qs('.product-form__input.product-form__quantity input', this.pi);
    return Math.max(1, parseInt(inp && inp.value, 10) || 1);
  };

  /* The quantity-tier ladder the savings bar shows (2+ / 3+ / 4+ -> 15/20/25%).
     Read off the live bar so a merchant edit to the bar cannot desync us; the
     theme's own defaults are the fallback. The automatic quantity discount
     applies to the silver product too, so the bar stays truthful. */
  var DEFAULT_TIERS = [{ minQty: 2, decimal: 0.15 }, { minQty: 3, decimal: 0.20 }, { minQty: 4, decimal: 0.25 }];

  Silver.prototype.tierDecimal = function (qty) {
    if (!this.cfg.qtyPricing) return 0;
    var list = [];
    var b = document.getElementById('ring-discount-bar');
    if (b) {
      qsa('.rdb-step[data-tier]', b).forEach(function (step) {
        var t = parseInt(step.getAttribute('data-tier'), 10);
        if (!t) return;
        var lab = qs('.rdb-label', step);
        var m = lab && lab.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
        if (m) list.push({ minQty: t + 1, decimal: parseFloat(m[1]) / 100 });
      });
    }
    /* No visual bar: take the ladder off the theme's own config holder, the
       same "15,20,25" -> qty 2 / 3 / 4+ mapping its script reads, so a merchant
       edit there moves steel and silver together. */
    if (!list.length && this.cfg.qtyCfg) {
      (this.cfg.qtyCfg.getAttribute('data-tiers') || '')
        .split(',')
        .forEach(function (pct, i) {
          var d = parseFloat(pct) / 100;
          if (d > 0) list.push({ minQty: i + 2, decimal: d });
        });
    }
    if (!list.length) list = DEFAULT_TIERS;
    var d = 0;
    list.forEach(function (t) { if (qty >= t.minQty) d = t.decimal; });
    return d;
  };

  /* ---------- capture ----------
     Field by field, the first pass in which each element exists, and always
     before that element is written to. v1 captured everything in one go, so a
     field that had not rendered yet was never restored. */
  Silver.prototype.captureOriginals = function () {
    var pi = this.pi;
    if (!pi) return;
    var orig = this.orig;

    var grab = function (key, sel, read, root) {
      if (orig[key] !== undefined) return;
      var el = qs(sel, root || pi);
      if (el) orig[key] = read(el);
    };
    var text = function (el) { return el.textContent.trim(); };

    if (this.steelVariantId === null) {
      var idInput = qs('form[action*="/cart/add"] input[name="id"]', pi);
      if (idInput && idInput.value && String(idInput.value) !== String(this.cfg.variantId)) {
        this.steelVariantId = idInput.value;
      }
    }

    grab('sale', '.price__sale .price-item--sale', text);
    grab('compare', '.price__sale s.price-item--regular', text);
    grab('regular', '.price__regular .price-item--regular', text);
    grab('badge', '.discount_price', text);
    grab('subtitle', '.custom-description', function (el) { return el.innerHTML; });
    grab('legend', 'fieldset.option_1 .selected_option_value', function (el) { return el.textContent; });

    var tabItem = qs('.product_tab_box .product_tab_box_item', pi);
    if (tabItem) {
      grab('tabHeading', 'h3', function (el) { return el.textContent; }, tabItem);
      grab('tabCopy', 'p', function (el) { return el.innerHTML; }, tabItem);
    }
    this.captured = orig.sale !== undefined;
  };

  /* ---------- individual swaps ---------- */

  Silver.prototype.applyPrice = function (silver) {
    if (!this.captured) return;
    /* On steel the theme owns the price (the quantity-tier bar rewrites it), so
       only restore once, on the transition back from silver. */
    if (!silver) {
      if (this.priceState !== 'silver') return;
      this.priceState = 'steel';
    } else {
      this.priceState = 'silver';
    }
    var pi = this.pi;
    var saleTxt, compareTxt, regularTxt, badgeTxt;

    if (silver) {
      var q = this.qty();
      var total = this.cfg.price * q * (1 - this.tierDecimal(q));
      var compareTotal = this.cfg.compareAt ? this.cfg.compareAt * q : 0;
      saleTxt = this.money(total);
      regularTxt = saleTxt;
      compareTxt = compareTotal ? this.money(compareTotal) : null;
      /* A 50%-off product on the 15% tier lands on an exact .5 percent, where
         float64 tips steel and silver to different sides of Math.round and the
         badge flickers 57/58 as the shopper switches material. Nudge both onto
         the true half-up value. */
      badgeTxt = compareTotal > 0
        ? Math.round((1 - total / compareTotal) * 100 + 1e-9) + '% OFF'
        : (this.cfg.percentOff ? this.cfg.percentOff + '% OFF' : null);
    } else {
      saleTxt = this.orig.sale;
      regularTxt = this.orig.regular;
      compareTxt = this.orig.compare;
      badgeTxt = this.orig.badge;
    }

    setText(qs('.price__sale .price-item--sale', pi), saleTxt);
    setText(qs('.price__regular .price-item--regular', pi), regularTxt);
    setText(qs('.price__sale s.price-item--regular', pi), compareTxt);
    var badge = qs('.discount_price', pi);
    if (badge && badgeTxt && badge.textContent.trim() !== badgeTxt) badge.textContent = badgeTxt;
  };

  /* Back on steel: let the theme's quantity-tier bar recompute its own prices
     (its unit price lives in a closure, so it only repaints on an event). */
  Silver.prototype.nudgeQtyBar = function () {
    if (!this.cfg.qtyPricing) return;
    var inp = qs('.product-form__input.product-form__quantity input', this.pi);
    if (!inp) return;
    try { inp.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
  };

  Silver.prototype.applyCopy = function (silver) {
    if (!this.captured) return;
    var pi = this.pi;
    var cfg = this.cfg;

    var legend = qs('fieldset.option_1 .selected_option_value', pi);
    if (legend) {
      var wantLegend = silver ? cfg.label : this.orig.legend;
      if (wantLegend != null && legend.textContent !== wantLegend) legend.textContent = wantLegend;
    }

    if (cfg.subtitle) {
      var sub = qs('.custom-description', pi);
      /* A rich-text metafield already arrives as <p>…</p>; a plain one does not. */
      var subHTML = cfg.subtitle.charAt(0) === '<' ? cfg.subtitle : '<p>' + cfg.subtitle + '</p>';
      if (sub) setHTML(sub, silver ? subHTML : this.orig.subtitle);
    }

    var tabItem = qs('.product_tab_box .product_tab_box_item', pi);
    if (tabItem) {
      var h = qs('h3', tabItem);
      var p = qs('p', tabItem);
      if (h && cfg.tabHeading) {
        var wantH = silver ? cfg.tabHeading : this.orig.tabHeading;
        if (wantH != null && h.textContent !== wantH) h.textContent = wantH;
      }
      if (p && cfg.tabCopy) setHTML(p, silver ? cfg.tabCopy : this.orig.tabCopy);
    }
  };

  /* True when a steel-only copy edit should run at all.

     "Extra Savings On 2+ Items Purchased" and the "do I get discounts if I buy
     more than 1 ring" FAQ are only WRONG on silver when the silver twin has no
     quantity pricing. On a PDP where both materials reprice by quantity (the
     automatic discount applies to the twin too) the line stays true and must be
     left alone — the Aug 2026 launch had to set four template fields by hand to
     express that. Decide it from the page instead. */
  Silver.prototype.steelCopyGated = function () {
    if (!this.cfg.qtyGatedCopy) return true;   // explicit config wins
    return !this.cfg.qtyPricing;
  };

  /* Icon list line, e.g. "Extra Savings On 2+ Items Purchased" is steel-only. */
  Silver.prototype.applyIconLine = function (silver) {
    var find = this.cfg.iconFind;
    var rep = this.cfg.iconReplace;
    if (!find || !rep) return;
    if (!this.steelCopyGated()) return;
    qsa('.product_icon_list li.product_icon', this.pi).forEach(function (li) {
      for (var n = li.firstChild; n; n = n.nextSibling) {
        if (n.nodeType !== 3) continue;
        if (n.__cwOrig === undefined) {
          if (n.nodeValue.indexOf(find) === -1) continue;
          n.__cwOrig = n.nodeValue;
        }
        var want = silver ? n.__cwOrig.replace(find, rep) : n.__cwOrig;
        if (n.nodeValue !== want) n.nodeValue = want;
      }
    });
  };

  Silver.prototype.applyCart = function (silver) {
    var id = silver ? String(this.cfg.variantId) : this.steelVariantId;
    if (!id) return;
    qsa('form[action*="/cart/add"] input[name="id"]', this.pi).forEach(function (inp) {
      if (inp.value !== id) inp.value = id;
    });
  };

  /* The quantity-savings bar prices the STEEL ladder. v2 needed a
     `cw-hide-on-silver` class hand-added to the bar in each cloned section;
     tag it from here so an untouched section works too. */
  Silver.prototype.markQtyBar = function () {
    if (!this.cfg.hideQtyBar) return;
    /* Same gate as the steel-only copy. The automatic quantity discount applies
       to the silver twin too, so on a product that prices by quantity the bar is
       still TRUE on silver and hiding it just deletes a working offer element
       from under the Add to Cart. Where it would be untrue there is no bar to
       hide anyway, so this only ever removes a wrong removal. */
    if (!this.steelCopyGated()) return;
    ['ring-discount-bar', 'ring-discount-bar-copy'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && !el.classList.contains('cw-hide-on-silver')) el.classList.add('cw-hide-on-silver');
    });
  };

  Silver.prototype.applySwatches = function (silver) {
    qsa('.cw-swatch', this.root).forEach(function (b) {
      var on = b.getAttribute('data-cw-mat') === (silver ? 'silver' : 'steel');
      if (b.classList.contains('cw-selected') !== on) b.classList.toggle('cw-selected', on);
      var want = on ? 'true' : 'false';
      if (b.getAttribute('aria-pressed') !== want) b.setAttribute('aria-pressed', want);
    });
  };

  /* ---- page-level swaps: only when the material changes, never on a timer -- */

  Silver.prototype.applyFaq = function (silver) {
    var rules = this.cfg.faq || [];
    var hideFaq = this.steelCopyGated() ? this.cfg.hideFaq : '';
    if (!rules.length && !hideFaq) return true;

    var accs = qsa('.frequently_section .accordion.set');
    if (!accs.length) return false;

    accs.forEach(function (acc) {
      var q = qs('.product_accordion', acc);
      var a = qs('.contents', acc);
      if (!q) return;
      var qText = q.textContent;

      rules.forEach(function (rule) {
        if (!rule || !rule.match) return;
        if (qText.toLowerCase().indexOf(String(rule.match).toLowerCase()) === -1) return;

        if (rule.question) {
          for (var n = q.firstChild; n; n = n.nextSibling) {
            if (n.nodeType === 3 && /\S/.test(n.nodeValue)) {
              if (acc.__cwOrigQ === undefined) acc.__cwOrigQ = n.nodeValue;
              var wantQ = silver ? ' ' + rule.question + ' ' : acc.__cwOrigQ;
              if (n.nodeValue !== wantQ) n.nodeValue = wantQ;
              break;
            }
          }
        }
        if (rule.answer && a) {
          if (acc.__cwOrigA === undefined) acc.__cwOrigA = a.innerHTML;
          setHTML(a, silver ? rule.answer : acc.__cwOrigA);
        }
      });

      /* The quantity-discount question only applies to the steel product. */
      if (hideFaq && qText.toLowerCase().indexOf(String(hideFaq).toLowerCase()) !== -1) {
        if (acc.classList.contains('cw-hidden') !== silver) acc.classList.toggle('cw-hidden', silver);
      }
    });
    return true;
  };

  function swapImages(imgs, list, offset) {
    imgs.forEach(function (img, i) {
      if (!img.__cwOrig) {
        img.__cwOrig = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
      }
      var entry = list ? list[(i + (offset || 0)) % list.length] : null;
      setAttr(img, 'src', entry ? entry.src : img.__cwOrig.src);
      if (img.__cwOrig.srcset != null) {
        setAttr(img, 'srcset', entry ? entry.srcset : img.__cwOrig.srcset);
      }
    });
  }

  Silver.prototype.applyGallery = function (silver) {
    var imgs = this.cfg.images || {};
    if (!imgs.main || !imgs.main.length) return true;
    var sid = this.cfg.sectionId;

    var items = qsa('[id^="Slider-Gallery-' + sid + '"] .product__media-item');
    if (!items.length) return false;

    items.forEach(function (item, i) {
      var entry = silver ? [imgs.main[i % imgs.main.length]] : null;
      var list = qsa('.product__media img', item);
      if (!list.length) list = qsa('img', item);
      swapImages(list, entry);
    });
    qsa('[id^="Slider-Thumbnails-' + sid + '"] .thumbnail').forEach(function (t, i) {
      var entry = silver && imgs.thumb && imgs.thumb.length ? [imgs.thumb[i % imgs.thumb.length]] : null;
      swapImages(qsa('img', t), entry);
    });
    swapImages(
      qsa('#ProductModal-' + sid + ' img, product-modal img, .product-media-modal img'),
      silver ? imgs.modal : null
    );
    return true;
  };

  /* The two description images below the buy box. Block 1 is the steel wordmark,
     block 2 the lifestyle photo — both show a steel ring, so a silver page that
     leaves them alone contradicts the material the shopper picked. Each block is
     swapped only when its own URL is configured. */
  Silver.prototype.applyImageBlock = function (silver) {
    var blocks = [
      { sel: '.image_block_item img', want: this.cfg.imageBlock },
      { sel: '.image_block_item_2 img', want: this.cfg.imageBlock2 }
    ];
    var ok = true;
    blocks.forEach(function (b) {
      if (!b.want) return;
      var imgs = qsa(b.sel);
      if (!imgs.length) { ok = false; return; }
      imgs.forEach(function (img) {
        if (!img.__cwOrigBlock) {
          img.__cwOrigBlock = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
        }
        if (silver) {
          setAttr(img, 'src', b.want);
          if (img.getAttribute('srcset')) img.removeAttribute('srcset');
        } else {
          setAttr(img, 'src', img.__cwOrigBlock.src);
          setAttr(img, 'srcset', img.__cwOrigBlock.srcset);
        }
      });
    });
    return ok;
  };

  /* One querySelector: has a theme re-render undone the gallery swap? */
  Silver.prototype.galleryReverted = function () {
    var imgs = this.cfg.images || {};
    if (this.state !== 'silver' || !imgs.main || !imgs.main.length) return false;
    var first = qs('[id^="Slider-Gallery-' + this.cfg.sectionId + '"] .product__media-item .product__media img');
    if (!first) return false;
    var want = String(imgs.main[0].src).split('?')[0];
    return (first.getAttribute('src') || '').indexOf(want) === -1;
  };

  Silver.prototype.pageWorkPending = function () {
    var d = this.pageDone;
    if (d.faq !== this.state || d.gallery !== this.state || d.block !== this.state) {
      return this.pageRetries < MAX_PAGE_RETRIES;
    }
    return this.galleryReverted();
  };

  Silver.prototype.applyPage = function (silver) {
    var d = this.pageDone;
    if (this.applyFaq(silver)) d.faq = this.state;
    if (this.applyGallery(silver)) d.gallery = this.state;
    if (this.applyImageBlock(silver)) d.block = this.state;
    if (d.faq !== this.state || d.gallery !== this.state || d.block !== this.state) this.pageRetries++;
    else this.pageRetries = 0;
  };

  /* ---------- one pass ---------- */

  Silver.prototype.apply = function (reason) {
    if (!this.pi || !this.pi.isConnected) this.pi = qs('product-info', this.root) || qs('product-info');
    if (!this.pi) return;

    this.captureOriginals();

    var silver = this.state === 'silver';
    window.__cwSilverActive = silver;
    if (document.body && document.body.classList.contains('cw-silver-active') !== silver) {
      document.body.classList.toggle('cw-silver-active', silver);
    }

    this.markQtyBar();
    this.applySwatches(silver);
    this.applyCart(silver);
    this.applyPrice(silver);
    if (!silver && this.wasSilver) this.nudgeQtyBar();
    this.wasSilver = silver;
    this.applyCopy(silver);
    this.applyIconLine(silver);

    if (this.pageWorkPending()) this.applyPage(silver);
    log('pass:', reason, '->', this.state);
  };

  /* ---------- scheduling: coalesced, re-entrancy-safe, rate-limited -------- */

  Silver.prototype.schedule = function (reason) {
    var self = this;
    if (this.queued || this.writing) return;
    this.queued = true;
    raf(function () { self.queued = false; self.run(reason); });
  };

  Silver.prototype.run = function (reason) {
    if (this.writing) return;

    var now = (window.performance && performance.now) ? performance.now() : +new Date();
    this.passes.push(now);
    while (this.passes.length && now - this.passes[0] > 1000) this.passes.shift();
    this.totalPasses++;
    if (this.passes.length > MAX_PASSES_PER_SECOND && this.totalPasses > 60 && !this.degraded) {
      this.degraded = true;
      this.stopObserving();
      warn('too many re-apply passes in one second — DOM observer disabled; the swatch still works');
      return;
    }

    this.writing = true;
    this.stopObserving();
    try { this.apply(reason); } catch (e) { warn(e); }
    this.writing = false;
    this.startObserving();
  };

  Silver.prototype.startObserving = function () {
    if (!this.observerAllowed || this.degraded || !this.mo) return;
    var pi = this.pi || qs('product-info', this.root);
    if (!pi) return;
    try { this.mo.observe(pi, { childList: true, subtree: true }); } catch (e) {}
  };
  Silver.prototype.stopObserving = function () {
    if (!this.mo) return;
    try { this.mo.disconnect(); } catch (e) {}
  };

  Silver.prototype.select = function (key) {
    if (key !== 'steel' && key !== 'silver') return;
    if (this.state === key) return;
    if (key === 'silver' && this.cfg.available === false) return;
    this.state = key;
    log('material ->', key);
    this.run('select');
  };

  Silver.prototype.debug = function () {
    return {
      version: VERSION, state: this.state, degraded: this.degraded,
      totalPasses: this.totalPasses, qtyPricing: this.cfg.qtyPricing,
      steelVariantId: this.steelVariantId, silverVariantId: this.cfg.variantId,
      captured: this.captured, orig: this.orig
    };
  };

  Silver.prototype.init = function () {
    var self = this;
    if (this.root && this.root.classList) this.root.classList.add('cw-silver-on');

    /* Flag the option-1 fieldset so the theme's own single-value swatch row is
       hidden even on a browser without :has() support (see the stylesheet). */
    var fs = qs('fieldset.option_1', this.root) || qs('fieldset.option_1');
    if (fs && !fs.classList.contains('cw-has-silver')) fs.classList.add('cw-has-silver');

    /* One delegated capture-phase listener: survives Dawn re-rendering the
       variant picker (which throws the swatch buttons away). */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest('.cw-swatch[data-cw-mat]');
      if (!btn || !self.contains(btn)) return;
      if (btn.disabled || btn.classList.contains('cw-unavailable')) return;
      e.preventDefault();
      e.stopPropagation();
      self.select(btn.getAttribute('data-cw-mat'));
    }, true);

    /* Quantity / variant changes: re-apply after the theme has had its turn.
       Deliberately narrow — typing in the engraving field must not re-run us. */
    ['change', 'input'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var isQty = !!t.closest('quantity-input') || t.name === 'quantity';
        var isVariant = t.tagName === 'SELECT' || (t.tagName === 'INPUT' && t.type === 'radio');
        if (!isQty && !isVariant) return;
        if (self.contains(t) || t.closest('#ring-discount-bar')) {
          setTimeout(function () { self.schedule(evt); }, 60);
        }
      }, true);
    });

    window.addEventListener('pageshow', function () { self.schedule('pageshow'); });

    if (typeof MutationObserver === 'function') {
      this.mo = new MutationObserver(function () { self.schedule('mutation'); });
    }

    this.run('init');

    var ready = function () {
      self.observerAllowed = true;              // never watch a streaming DOM
      self.startObserving();
      self.schedule('ready');
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ready, { once: true });
    } else {
      ready();
    }
  };

  /* ==========================================================================
   * Boot
   * ======================================================================== */

  /* Reads the runtime facts the config cannot know at render time. */
  function runtimeFlags(cfg) {
    /* Quantity pricing is on when the theme prices by quantity. Two shapes:
       the visual #ring-discount-bar (the PDPs that still show it) and, since the
       21 Aug rollout, a hidden #QtyPricing-<sectionId> config holder on every
       other product. Either one means steel repricing on quantity, so silver has
       to reprice too or the two materials disagree. */
    cfg.qtyCfg = document.getElementById('QtyPricing-' + cfg.sectionId);
    cfg.qtyPricing = !!qs('#ring-discount-bar') || !!cfg.qtyCfg;
    return cfg;
  }

  function boot(holder) {
    if (!holder || holder.getAttribute(ATTR) === 'true') return;
    var cfg;
    try { cfg = JSON.parse(holder.textContent); } catch (e) { warn('bad config JSON'); return; }
    if (!cfg || !cfg.variantId) return;
    holder.setAttribute(ATTR, 'true');

    /* Every copy field is written straight into the DOM, so anything that is not
       a string has to be dropped here rather than stringified downstream — a
       Liquid slip that leaves a metafield OBJECT in the config used to surface as
       the literal "[object Object]" in the sub-title. Empty means "don't swap". */
    ['subtitle', 'tabHeading', 'tabCopy', 'label', 'hideFaq', 'iconFind', 'iconReplace']
      .forEach(function (k) {
        if (cfg[k] != null && typeof cfg[k] !== 'string') {
          warn('config.' + k + ' is not a string — ignoring it');
          cfg[k] = '';
        }
      });

    /* The config now lives inside the variant picker, which a variant change can
       re-render — and a re-inserted <script src> re-executes this whole file.
       Keyed by section id, a second config for a PDP we already control just
       refreshes the live instance. */
    var key = String(cfg.sectionId);
    var live = window.__cwSilverBySection[key];
    if (live) {
      live.cfg = runtimeFlags(cfg);
      live.pageDone = { faq: null, gallery: null, block: null };
      live.pageRetries = 0;
      live.schedule('re-render');
      log('re-render picked up for section', key);
      return;
    }

    /* Scope: the enclosing section, so swatch clicks and <product-info> lookups
       stay inside this product even on a page with several product sections. */
    var scope = document.getElementById('shopify-section-' + cfg.sectionId) ||
                (holder.closest ? holder.closest('.shopify-section') : null) ||
                document;

    runtimeFlags(cfg);
    var inst = new Silver(scope, cfg);
    if (scope !== document) scope.__cwSilver = inst;
    window.__cwSilverBySection[key] = inst;
    window.__cwSilverInstances.push(inst);
    inst.init();
    log('installed v' + VERSION + ' (' + cfg.source + ') on section ' + key);
  }

  function bootAll(scope) {
    qsa('[data-cw-silver-config]', scope || document).forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(); }, { once: true });
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });
})();
